/**
 * EvidenceEventRow
 *
 * Compact row component for displaying evidence or governance events.
 * Shows type indicator, title, timestamp, and optional status.
 */
import React from 'react';
import {
  makeStyles,
  tokens,
  Body1,
  Body2,
} from '@fluentui/react-components';
import { governanceTokens } from './design-tokens';
import { categoryColors } from '../../theme/solution-theme';

export type EventType = 'evidence' | 'enforcement' | 'scan' | 'policy' | 'alert';

export interface EvidenceEventRowProps {
  type: EventType;
  title: string;
  description?: string;
  timestamp: Date | string;
  status?: 'success' | 'warning' | 'error';
  onClick?: () => void;
}

const typeColors: Record<EventType, string> = {
  evidence: governanceTokens.colorGovernancePrimary,
  enforcement: governanceTokens.colorGovernanceSecondary,
  // #E852B9 === brand-config's "Code Magenta" category colour.
  scan: categoryColors.code,
  policy: governanceTokens.colorStatusWarning,
  alert: governanceTokens.colorStatusError,
};

const statusDotColors: Record<string, string> = {
  success: governanceTokens.colorStatusSuccess,
  warning: governanceTokens.colorStatusWarning,
  error: governanceTokens.colorStatusError,
};

const useStyles = makeStyles({
  row: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalM,
    padding: `${tokens.spacingVerticalS} ${tokens.spacingHorizontalM}`,
    borderRadius: tokens.borderRadiusMedium,
    transition: 'background-color 0.1s ease',
    ':hover': {
      backgroundColor: tokens.colorNeutralBackground1Hover,
    },
  },
  clickable: {
    cursor: 'pointer',
  },
  typeIndicator: {
    width: '8px',
    height: '8px',
    borderRadius: '50%',
    flexShrink: 0,
  },
  content: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalXXS,
    flex: 1,
    minWidth: 0,
  },
  title: {
    fontWeight: tokens.fontWeightMedium,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  description: {
    color: tokens.colorNeutralForeground3,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  rightSection: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalS,
    flexShrink: 0,
  },
  statusDot: {
    width: '6px',
    height: '6px',
    borderRadius: '50%',
  },
  timestamp: {
    color: tokens.colorNeutralForeground3,
    whiteSpace: 'nowrap',
    fontSize: tokens.fontSizeBase200,
  },
});

function formatRelativeTime(date: Date | string): string {
  const now = new Date();
  const then = typeof date === 'string' ? new Date(date) : date;
  const diffMs = now.getTime() - then.getTime();
  const diffSec = Math.floor(diffMs / 1000);

  if (diffSec < 60) return 'Just now';
  const diffMin = Math.floor(diffSec / 60);
  if (diffMin < 60) return `${diffMin}m ago`;
  const diffHr = Math.floor(diffMin / 60);
  if (diffHr < 24) return `${diffHr}h ago`;
  const diffDay = Math.floor(diffHr / 24);
  if (diffDay < 7) return `${diffDay}d ago`;
  return then.toLocaleDateString();
}

export const EvidenceEventRow: React.FC<EvidenceEventRowProps> = ({
  type,
  title,
  description,
  timestamp,
  status,
  onClick,
}) => {
  const styles = useStyles();

  return (
    <div
      className={`${styles.row} ${onClick ? styles.clickable : ''}`}
      onClick={onClick}
      role={onClick ? 'button' : undefined}
      tabIndex={onClick ? 0 : undefined}
      onKeyDown={onClick ? (e) => { if (e.key === 'Enter' || e.key === ' ') onClick(); } : undefined}
    >
      <div
        className={styles.typeIndicator}
        style={{ backgroundColor: typeColors[type] }}
      />
      <div className={styles.content}>
        <Body1 className={styles.title}>{title}</Body1>
        {description && (
          <Body2 className={styles.description}>{description}</Body2>
        )}
      </div>
      <div className={styles.rightSection}>
        {status && (
          <div
            className={styles.statusDot}
            style={{ backgroundColor: statusDotColors[status] }}
          />
        )}
        <Body2 className={styles.timestamp}>
          {formatRelativeTime(timestamp)}
        </Body2>
      </div>
    </div>
  );
};
